import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef } from 'react';
import { CreditCardIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
export type PaymentMethod = 'all' | 'card' | 'bank_transfer' | 'ewallet' | 'bnpl' | 'crypto';
interface PaymentMethodFilterProps {
  value: PaymentMethod[];
  onChange: (methods: PaymentMethod[]) => void;
}
export function PaymentMethodFilter({
  value,
  onChange
}: PaymentMethodFilterProps) {
  const {
    t
  } = useTranslation("analytics");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const options: {
    value: PaymentMethod;
    label: string;
  }[] = [{
    value: 'card',
    label: 'Card'
  }, {
    value: 'bank_transfer',
    label: 'Bank transfer'
  }, {
    value: 'ewallet',
    label: 'E-wallet'
  }, {
    value: 'bnpl',
    label: 'BNPL'
  }, {
    value: 'crypto',
    label: 'Crypto'
  }];
  const toggleMethod = (method: PaymentMethod) => {
    if (value.includes(method)) {
      onChange(value.filter(m => m !== method));
    } else {
      onChange([...value, method]);
    }
  };
  const selectAll = () => {
    onChange(options.map(opt => opt.value));
  };
  const clearAll = () => {
    onChange([]);
  };
  const displayText = () => {
    if (value.length === 0) {
      return 'All payment methods';
    } else if (value.length === options.length) {
      return 'All payment methods';
    } else if (value.length === 1) {
      return options.find(opt => opt.value === value[0])?.label || 'Payment method';
    } else {
      return `${value.length} payment methods`;
    }
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1991" data-mp="1tyvn3ol56wq9">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1992" data-mp="1txc2jlsgdzio">
        
        <CreditCardIcon size={16} data-id="element-1993" data-mp="23wjrz7cgpkm5" />
        <span data-id="element-1994" data-mp="1yy8emmmykrz6">{displayText()}</span>
        <ChevronDownIcon size={16} data-id="element-1995" data-mp="1yz9vj7qno8ot" />
      </button>

      {isOpen && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50" data-id="element-1996" data-mp="1b8nnvhkkcqfc">
          {/* Select/Clear all */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200" data-id="element-1997" data-mp="16avs2cgssk5v">
            <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-1998" data-mp="1kz8rxkj0an5n">
            
              {t("components.PaymentMethodFilter.select_all")}
            </button>
            <button onClick={clearAll} className="text-xs text-gray-600 hover:text-gray-700 font-medium" data-id="element-1999" data-mp="sglcolqoy2ch">
            
              {t("components.PaymentMethodFilter.clear_all")}
            </button>
          </div>

          {/* Options */}
          <div className="py-1" data-id="element-2000" data-mp="33x5337u1di02">
            {options.map(option => {
          const isSelected = value.includes(option.value);
          return <button key={option.value} onClick={() => toggleMethod(option.value)} className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-100" data-id="element-2001" data-mp="2q6rur4d8g1a7">
                
                  <span className={isSelected ? 'text-blue-600 font-medium' : 'text-gray-700'} data-id="element-2002" data-mp="3hwoafyf0wpbb">
                  
                    {option.label}
                  </span>
                  {isSelected && <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center" data-id="element-2003" data-mp="38wrgbh9v6y8v">
                      <CheckIcon size={12} className="text-white" data-id="element-2004" data-mp="3dut6sn9mdzud" />
                    </div>}
                </button>;
        })}
          </div>

          {/* Apply button */}
          <div className="border-t border-gray-200 p-2" data-id="element-2005" data-mp="22g5y098oi3fi">
            <button onClick={() => setIsOpen(false)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-2006" data-mp="22hqtc12thqef">
            
              {t("components.content.apply")}
            </button>
          </div>
        </div>}
    </div>;
}